import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check, X, Loader2, Plus, ChevronLeft, ChevronRight, Camera, AlertCircle } from 'lucide-react'
import { api } from '../../lib/api'

interface QuickScanResultProps {
  images: string[]  // Array of base64 images (supports multi-page)
  studentId: number
  onConfirm: () => void
  onEdit: (scanId: number) => void
  onCancel: () => void
  onAddPage: () => void  // Callback to add another page
}

export function QuickScanResult({
  images,
  studentId,
  onConfirm,
  onCancel,
  onAddPage,
}: QuickScanResultProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Handle save - wait for confirmation from server
  const handleSave = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Wait for the API to confirm receipt
      const response = await api.post(`/api/parent/students/${studentId}/quick-scan`, {
        image_data: images[0],
        additional_pages: images.slice(1),
        page_count: images.length,
      })

      // Success - refresh queries and close
      queryClient.invalidateQueries({ queryKey: ['parent-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['parent-dashboard-homework'] })
      queryClient.invalidateQueries({ queryKey: ['parent-scans-overview'] })
      queryClient.invalidateQueries({ queryKey: ['child-scan-history', studentId] })
      queryClient.invalidateQueries({ queryKey: ['child-scan-stats', studentId] })

      console.log('Scan saved successfully:', response.data)
      onConfirm()
    } catch (err) {
      console.error('Scan upload failed:', err)
      setIsSubmitting(false)

      // Extract error message
      const errorMessage = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data?.message ||
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save scan. Please try again.'
      setError(errorMessage)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-surface-base/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-surface-card">
        <div>
          <h2 className="text-lg font-semibold">Review Scan</h2>
          <p className="text-sm text-gray-400">
            {images.length} {images.length === 1 ? 'page' : 'pages'} captured
          </p>
        </div>
        <button
          onClick={onCancel}
          className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Image preview with page navigation */}
        <div className="card p-2">
          <div className="relative">
            <img
              src={images[currentPage]}
              alt={`Page ${currentPage + 1}`}
              className="w-full max-h-64 object-contain rounded"
            />

            {/* Page navigation for multi-page */}
            {images.length > 1 && (
              <>
                {currentPage > 0 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-surface-card/80 rounded-full hover:bg-surface-hover"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                {currentPage < images.length - 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-surface-card/80 rounded-full hover:bg-surface-hover"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-surface-card/80 rounded text-sm">
                  Page {currentPage + 1} of {images.length}
                </div>
              </>
            )}
          </div>

          {/* Page thumbnails */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx)}
                className={`flex-shrink-0 w-14 h-14 rounded border-2 overflow-hidden ${
                  idx === currentPage ? 'border-quest-primary' : 'border-gray-700/50'
                }`}
              >
                <img src={img} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
            {/* Add page button in thumbnails */}
            <button
              onClick={onAddPage}
              className="flex-shrink-0 w-14 h-14 rounded border-2 border-dashed border-gray-600 hover:border-quest-primary hover:bg-surface-hover flex items-center justify-center text-gray-500 hover:text-quest-primary transition-colors"
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Add another page - prominent button */}
        <button
          onClick={onAddPage}
          disabled={isSubmitting}
          className="w-full card p-4 flex items-center justify-center gap-3 text-gray-300 hover:text-white hover:bg-surface-hover transition-colors border-dashed border-2 border-gray-600 hover:border-quest-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Camera className="w-5 h-5" />
          <span className="font-medium">Add Another Page</span>
        </button>

        {/* Error message */}
        {error && (
          <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-medium">Upload Failed</p>
              <p className="text-red-400/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Info text */}
        {!error && (
          <p className="text-center text-sm text-gray-500">
            Add all pages of your document, then tap Save
          </p>
        )}
      </div>

      {/* Footer actions */}
      <div className="p-4 bg-surface-card border-t border-gray-700/50">
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="btn-secondary flex-1 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className={`flex-1 flex items-center justify-center gap-2 ${
              error ? 'bg-orange-600 hover:bg-orange-700' : 'btn-primary'
            } rounded-lg py-3 font-medium transition-colors disabled:opacity-50`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : error ? (
              <>
                <AlertCircle className="w-5 h-5" />
                Retry
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Save{images.length > 1 ? ` (${images.length} pages)` : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
